
import React from 'react';
import BaseChart from './common/BaseChart';
import BarChartComponent from './common/BarChartComponent';
import { Badge } from '@/components/ui/badge';
import ChartSkeleton from '../common/ChartSkeleton';

interface OptimizedPageViewsChartProps {
  pageStats: any[];
  loading: boolean;
  exportCSV: (data: any[], filename: string) => void;
  title?: string;
  description?: string;
  exportFilename?: string;
}

const OptimizedPageViewsChart = React.memo(({ 
  pageStats, 
  loading, 
  exportCSV,
  title = "Page Views",
  description = "Distribution of views across different pages",
  exportFilename = "page_stats"
}: OptimizedPageViewsChartProps) => {
  // Memoize the consolidated data processing
  const processedData = React.useMemo(() => {
    if (!Array.isArray(pageStats) || pageStats.length === 0) return [];
    
    const pageMap: Record<string, number> = {};
    
    pageStats.forEach(item => {
      let path = item.page_path;
      
      if (path.startsWith('/paper/')) {
        pageMap['Papers'] = (pageMap['Papers'] || 0) + item.count;
      } else if (path.startsWith('/upload/success')) {
        pageMap['Upload Success'] = (pageMap['Upload Success'] || 0) + item.count;
      } else if (path.startsWith('/author/')) {
        pageMap['Author Pages'] = (pageMap['Author Pages'] || 0) + item.count;
      } else if (path === '/' || path === '/index' || path === '/home') {
        pageMap['Home'] = (pageMap['Home'] || 0) + item.count;
      } else if (path === '/about') {
        pageMap['About'] = (pageMap['About'] || 0) + item.count;
      } else if (path.startsWith('/explore') || path === '/explore') {
        // Parse the explore page to determine display type
        if (path.includes('display=list') || path.includes('display_type=list')) {
          pageMap['Explore - List View'] = (pageMap['Explore - List View'] || 0) + item.count;
        } else if (path.includes('display=grid') || path.includes('display_type=grid')) {
          pageMap['Explore - Grid View'] = (pageMap['Explore - Grid View'] || 0) + item.count;
        } else {
          // Default explore page (no display parameter or other parameters)
          pageMap['Explore - Grid View'] = (pageMap['Explore - Grid View'] || 0) + item.count;
        }
      } else if (path === '/faq') {
        pageMap['FAQ'] = (pageMap['FAQ'] || 0) + item.count;
      } else if (path.includes('privacy') || path === '/privacy-policy') {
        pageMap['Privacy Policy'] = (pageMap['Privacy Policy'] || 0) + item.count;
      } else if (path.includes('terms') || path === '/terms-of-service') {
        pageMap['Terms of Service'] = (pageMap['Terms of Service'] || 0) + item.count;
      } else if (path.includes('cookie') || path === '/cookie-policy') {
        pageMap['Cookie Policy'] = (pageMap['Cookie Policy'] || 0) + item.count;
      } else if (path.includes('analytics') || path === '/analytics') {
        pageMap['Analytics'] = (pageMap['Analytics'] || 0) + item.count;
      } else if (path.startsWith('/invitations/') || path === '/invitations') {
        pageMap['Review Invitations'] = (pageMap['Review Invitations'] || 0) + item.count;
      } else if (path.startsWith('/unsubscribe')) {
        pageMap['Unsubscribe'] = (pageMap['Unsubscribe'] || 0) + item.count;
      } else {
        const formattedPath = path.replace(/^\//, '').replace(/-/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ') || 'Unknown Page';
        pageMap[formattedPath] = (pageMap[formattedPath] || 0) + item.count;
      }
    });
    
    return Object.entries(pageMap)
      .map(([page_path, count]) => ({ 
        page_path, 
        count 
      }))
      .sort((a, b) => b.count - a.count);
  }, [pageStats]);

  if (loading) {
    return (
      <ChartSkeleton 
        title={title}
        description={description}
        height={450}
        type="bar"
      />
    );
  }
  
  return (
    <BaseChart
      title={title}
      description={description}
      data={processedData}
      loading={false}
      exportCSV={exportCSV}
      exportFilename={exportFilename}
      chartType="bar"
    >
      <div className="mb-4 flex justify-center">
        <Badge variant="secondary" className="text-xs">
          Similar pages are grouped together for better visibility
        </Badge>
      </div>
      <BarChartComponent
        data={processedData}
        dataKey="count"
        nameKey="page_path"
        color="#4f46e5"
        formatter={(value) => [`${value}`, 'Views']}
        angle={-45}
        enableMultilineLabels={true}
        margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
      />
    </BaseChart>
  );
});

OptimizedPageViewsChart.displayName = 'OptimizedPageViewsChart';

export default OptimizedPageViewsChart;
